import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import type {
  VaccineCatalogItem,
  VaccineCatalogResponse,
  VaccinationBodySide,
  VaccinationListResponse,
  VaccinationRecord,
  VaccinationRecordStatus,
} from '../lib/vaccinations';
import { formatVaccinationDate } from '../lib/vaccinations';
import { SelectMenu } from '../ui/SelectMenu';

type VaccinationFormState = {
  recordId: string | null;
  vaccineCode: string;
  vaccineName: string;
  disease: string;
  status: VaccinationRecordStatus;
  administrationDate: string;
  recordedDate: string;
  doseNumber: string;
  seriesDoses: string;
  booster: boolean;
  route: string;
  site: string;
  bodySide: VaccinationBodySide | '';
  doseVolume: string;
  doseUnit: string;
  lotNumber: string;
  expirationDate: string;
  manufacturer: string;
  administeredBy: string;
  location: string;
  fundingSource: string;
  visDate: string;
  visEditionDate: string;
  nextDueDate: string;
  reaction: string;
  note: string;
  source: string;
  externalId: string;
};

const statusOptions: VaccinationRecordStatus[] = ['ADMINISTERED', 'SCHEDULED', 'PARTIAL', 'HISTORICAL', 'REFUSED', 'CONTRAINDICATED'];
const bodySideOptions: Array<VaccinationBodySide | ''> = ['', 'LEFT', 'RIGHT', 'BILATERAL'];

function toInputDateTime(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function nowForInput() {
  return toInputDateTime(new Date().toISOString());
}

function emptyForm(): VaccinationFormState {
  return {
    recordId: null,
    vaccineCode: '',
    vaccineName: '',
    disease: '',
    status: 'ADMINISTERED',
    administrationDate: nowForInput(),
    recordedDate: nowForInput(),
    doseNumber: '',
    seriesDoses: '',
    booster: false,
    route: 'IM',
    site: '',
    bodySide: '',
    doseVolume: '0.5',
    doseUnit: 'mL',
    lotNumber: '',
    expirationDate: '',
    manufacturer: '',
    administeredBy: '',
    location: '',
    fundingSource: '',
    visDate: '',
    visEditionDate: '',
    nextDueDate: '',
    reaction: '',
    note: '',
    source: 'clinic-administered',
    externalId: '',
  };
}

function toFormState(record: VaccinationRecord): VaccinationFormState {
  return {
    recordId: record.id,
    vaccineCode: record.vaccineCode || '',
    vaccineName: record.vaccineName || '',
    disease: record.disease || '',
    status: record.status,
    administrationDate: toInputDateTime(record.administrationDate),
    recordedDate: toInputDateTime(record.recordedDate),
    doseNumber: record.doseNumber !== null && record.doseNumber !== undefined ? String(record.doseNumber) : '',
    seriesDoses: record.seriesDoses !== null && record.seriesDoses !== undefined ? String(record.seriesDoses) : '',
    booster: Boolean(record.booster),
    route: record.route || '',
    site: record.site || '',
    bodySide: (record.bodySide as VaccinationBodySide | null) || '',
    doseVolume: record.doseVolume !== null && record.doseVolume !== undefined ? String(record.doseVolume) : '',
    doseUnit: record.doseUnit || '',
    lotNumber: record.lotNumber || '',
    expirationDate: toInputDateTime(record.expirationDate),
    manufacturer: record.manufacturer || '',
    administeredBy: record.administeredBy || '',
    location: record.location || '',
    fundingSource: record.fundingSource || '',
    visDate: toInputDateTime(record.visDate),
    visEditionDate: toInputDateTime(record.visEditionDate),
    nextDueDate: toInputDateTime(record.nextDueDate),
    reaction: record.reaction || '',
    note: record.note || '',
    source: record.source || '',
    externalId: record.externalId || '',
  };
}

function toInt(value: string): number | undefined {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.trunc(numeric) : undefined;
}

function toFloat(value: string): number | undefined {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function statusPill(status: VaccinationRecordStatus) {
  const map: Record<VaccinationRecordStatus, string> = {
    ADMINISTERED: 'bg-emerald-100 text-emerald-700',
    SCHEDULED: 'bg-blue-100 text-blue-700',
    PARTIAL: 'bg-amber-100 text-amber-800',
    HISTORICAL: 'bg-slate-100 text-slate-700',
    REFUSED: 'bg-rose-100 text-rose-700',
    CONTRAINDICATED: 'bg-rose-100 text-rose-700',
  };
  return map[status] || 'bg-slate-100 text-slate-700';
}

export function PatientVaccinationsModule({ patientId }: { patientId: string }) {
  const { fetchJson, fetchApi } = useApiClient();
  const { tr } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<VaccineCatalogItem[]>([]);
  const [records, setRecords] = useState<VaccinationRecord[]>([]);
  const [form, setForm] = useState<VaccinationFormState>(emptyForm());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | VaccinationRecordStatus>('ALL');
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return records.filter((row) => {
      if (statusFilter !== 'ALL' && row.status !== statusFilter) return false;
      if (!term) return true;
      const haystack = [row.vaccineName, row.vaccineCode || '', row.disease || '', row.manufacturer || '', row.lotNumber || ''].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [records, search, statusFilter]);

  const summary = useMemo(() => {
    const now = Date.now();
    const administered = records.filter((row) => row.status === 'ADMINISTERED' || row.status === 'HISTORICAL').length;
    const scheduled = records.filter((row) => row.status === 'SCHEDULED').length;
    const overdue = records.filter((row) => row.nextDueDate && new Date(row.nextDueDate).getTime() < now && row.status !== 'ADMINISTERED').length;
    return { total: records.length, administered, scheduled, overdue };
  }, [records]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [catalogRes, recordsRes] = await Promise.all([
        fetchJson<VaccineCatalogResponse>('/vaccinations/catalog'),
        fetchJson<VaccinationListResponse>(`/vaccinations/patients/${patientId}/records?limit=250`),
      ]);
      setCatalog(Array.isArray(catalogRes.data) ? catalogRes.data : []);
      setRecords(Array.isArray(recordsRes.data) ? recordsRes.data : []);
    } catch (err: any) {
      setError(err?.message || tr('Failed to load vaccination records.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, patientId, tr]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!dialogOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDialogOpen(false);
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [dialogOpen]);

  function applyCatalogPick(name: string) {
    const picked = catalog.find((row) => row.name === name || row.cvx === name);
    if (!picked) return;
    setForm((prev) => ({
      ...prev,
      vaccineCode: picked.cvx,
      vaccineName: picked.name,
      disease: prev.disease || picked.disease,
    }));
  }

  function resetForm() {
    setForm(emptyForm());
  }

  function openCreateDialog() {
    resetForm();
    setError(null);
    setDialogOpen(true);
  }

  async function saveRecord() {
    if (!form.vaccineName.trim()) {
      setError(tr('Vaccine name is required.'));
      return;
    }

    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const payload = {
        vaccineCode: form.vaccineCode || undefined,
        vaccineName: form.vaccineName.trim(),
        disease: form.disease || undefined,
        status: form.status,
        administrationDate: form.administrationDate ? new Date(form.administrationDate).toISOString() : undefined,
        recordedDate: form.recordedDate ? new Date(form.recordedDate).toISOString() : undefined,
        doseNumber: form.doseNumber ? toInt(form.doseNumber) : undefined,
        seriesDoses: form.seriesDoses ? toInt(form.seriesDoses) : undefined,
        booster: form.booster,
        route: form.route || undefined,
        site: form.site || undefined,
        bodySide: form.bodySide || undefined,
        doseVolume: form.doseVolume ? toFloat(form.doseVolume) : undefined,
        doseUnit: form.doseUnit || undefined,
        lotNumber: form.lotNumber || undefined,
        expirationDate: form.expirationDate ? new Date(form.expirationDate).toISOString() : undefined,
        manufacturer: form.manufacturer || undefined,
        administeredBy: form.administeredBy || undefined,
        location: form.location || undefined,
        fundingSource: form.fundingSource || undefined,
        visDate: form.visDate ? new Date(form.visDate).toISOString() : undefined,
        visEditionDate: form.visEditionDate ? new Date(form.visEditionDate).toISOString() : undefined,
        nextDueDate: form.nextDueDate ? new Date(form.nextDueDate).toISOString() : undefined,
        reaction: form.reaction || undefined,
        note: form.note || undefined,
        source: form.source || undefined,
        externalId: form.externalId || undefined,
      };

      const path = form.recordId
        ? `/vaccinations/patients/${patientId}/records/${form.recordId}`
        : `/vaccinations/patients/${patientId}/records`;
      const method = form.recordId ? 'PATCH' : 'POST';
      const response = await fetchApi(path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Save failed (${response.status})`);
      const saved = (await response.json()) as VaccinationRecord;

      setRecords((prev) => {
        const exists = prev.some((item) => item.id === saved.id);
        if (exists) return prev.map((item) => (item.id === saved.id ? saved : item));
        return [saved, ...prev];
      });

      setBanner(form.recordId ? tr('Vaccination record updated.') : tr('Vaccination record added.'));
      resetForm();
      setDialogOpen(false);
    } catch (err: any) {
      setError(err?.message || tr('Failed to save vaccination record.'));
    } finally {
      setSaving(false);
    }
  }

  function editRecord(record: VaccinationRecord) {
    setForm(toFormState(record));
    setBanner(null);
    setError(null);
    setDialogOpen(true);
  }

  async function deleteRecord(record: VaccinationRecord) {
    if (!window.confirm(tr('Delete vaccination record?'))) return;
    setBusyId(record.id);
    setError(null);
    setBanner(null);
    try {
      const response = await fetchApi(`/vaccinations/patients/${patientId}/records/${record.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`Delete failed (${response.status})`);
      setRecords((prev) => prev.filter((item) => item.id !== record.id));
      if (form.recordId === record.id) {
        resetForm();
        setDialogOpen(false);
      }
      setBanner(tr('Vaccination record deleted.'));
    } catch (err: any) {
      setError(err?.message || tr('Failed to delete vaccination record.'));
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">{tr('Loading vaccinations...')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Vaccinations')}</p>
          <h3 className="text-lg font-semibold text-slate-900">{tr('Patient Immunization Record')}</h3>
          <p className="text-xs text-slate-500">{tr('Capture administered/scheduled/refused vaccines with full traceability (lot, manufacturer, VIS, and next due date).')}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-2"><p className="text-slate-500 text-xs">{tr('Total')}</p><p className="font-semibold text-slate-800">{summary.total}</p></div>
          <div className="rounded-lg border border-slate-200 bg-emerald-50 p-2"><p className="text-emerald-700 text-xs">{tr('Administered/Historical')}</p><p className="font-semibold text-emerald-800">{summary.administered}</p></div>
          <div className="rounded-lg border border-slate-200 bg-blue-50 p-2"><p className="text-blue-700 text-xs">{tr('Scheduled')}</p><p className="font-semibold text-blue-800">{summary.scheduled}</p></div>
          <div className="rounded-lg border border-slate-200 bg-amber-50 p-2"><p className="text-amber-700 text-xs">{tr('Potentially Overdue')}</p><p className="font-semibold text-amber-800">{summary.overdue}</p></div>
        </div>

        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
        {banner && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{banner}</div>}

        <div className="flex items-center justify-end">
          <button className="inline-flex items-center gap-2 rounded-lg bg-lime-400 px-3 py-2 text-sm text-slate-900 hover:bg-lime-300" onClick={openCreateDialog}>
            <Plus size={14} /> {tr('Add Vaccine')}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h4 className="text-sm font-semibold text-slate-800">{tr('Vaccination History')}</h4>
          <div className="flex items-center gap-2">
            <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder={tr('Search vaccine, disease, lot...')} value={search} onChange={(event) => setSearch(event.target.value)} />
            <select className="rounded-lg border border-slate-200 px-3 py-2 text-sm" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as 'ALL' | VaccinationRecordStatus)}>
              <option value="ALL">{tr('All statuses')}</option>
              {statusOptions.map((status) => (<option key={status} value={status}>{tr(status)}</option>))}
            </select>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500 border-y border-slate-100">
              <tr>
                <th className="py-2 pr-3 text-left">{tr('Date')}</th>
                <th className="py-2 pr-3 text-left">{tr('Vaccine')}</th>
                <th className="py-2 pr-3 text-left">{tr('Dose')}</th>
                <th className="py-2 pr-3 text-left">{tr('Status')}</th>
                <th className="py-2 pr-3 text-left">{tr('Lot / Manufacturer')}</th>
                <th className="py-2 pr-3 text-left">{tr('Next Due')}</th>
                <th className="py-2 pr-3 text-right">{tr('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td className="py-2 pr-3 text-slate-600">{formatVaccinationDate(row.administrationDate || row.recordedDate)}</td>
                  <td className="py-2 pr-3">
                    <p className="font-medium text-slate-900">{row.vaccineName}</p>
                    <p className="text-xs text-slate-500">{row.vaccineCode || tr('Custom')}{row.disease ? ` • ${row.disease}` : ''}</p>
                  </td>
                  <td className="py-2 pr-3 text-slate-700">
                    {row.doseNumber !== null && row.seriesDoses !== null ? `${row.doseNumber}/${row.seriesDoses}` : row.doseNumber !== null ? String(row.doseNumber) : '-'}
                    {row.booster ? ` • ${tr('Booster')}` : ''}
                  </td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusPill(row.status)}`}>{tr(row.status)}</span></td>
                  <td className="py-2 pr-3 text-slate-700">
                    <p>{row.lotNumber || '-'}</p>
                    <p className="text-xs text-slate-500">{row.manufacturer || '-'}</p>
                  </td>
                  <td className="py-2 pr-3 text-slate-700">{formatVaccinationDate(row.nextDueDate)}</td>
                  <td className="py-2 pr-3">
                    <div className="flex justify-end gap-2">
                      <button className="rounded border border-slate-200 px-2 py-1 text-xs" onClick={() => editRecord(row)}>{tr('Edit')}</button>
                      <button className="inline-flex items-center gap-1 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700 disabled:opacity-60" onClick={() => deleteRecord(row)} disabled={busyId === row.id}>
                        {busyId === row.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} {tr('Delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length ? (
                <tr>
                  <td className="py-5 text-center text-slate-500" colSpan={7}>{tr('No vaccination records found.')}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {dialogOpen && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4" onClick={() => setDialogOpen(false)}>
          <div className="mt-6 max-h-[92vh] w-full max-w-6xl overflow-auto rounded-[28px] border border-slate-200 bg-slate-50 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-500">{tr('Vaccination Form')}</p>
                    <h4 className="text-2xl font-semibold text-slate-950">{form.recordId ? tr('Edit Vaccination Record') : tr('Add Vaccination Record')}</h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StatChip label={tr('Status')} value={tr(form.status)} tone="blue" />
                    <StatChip label={tr('Vaccine')} value={form.vaccineName || tr('New record')} tone="emerald" />
                    <StatChip
                      label={tr('Dose')}
                      value={
                        form.doseNumber && form.seriesDoses
                          ? `${form.doseNumber}/${form.seriesDoses}`
                          : form.doseNumber || tr('Not set')
                      }
                      tone="amber"
                    />
                  </div>
                </div>
                <button className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50" onClick={() => setDialogOpen(false)}>
                  {tr('Close')}
                </button>
              </div>
            </div>

            <div className="space-y-5 p-5">
              <SectionCard
                title={tr('Vaccine Identity')}
                description={tr('Select a catalog entry or record the vaccine details manually.')}
              >
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
                  <label className="block space-y-1 xl:col-span-5">
                    <span className="text-xs uppercase tracking-wide text-slate-500">{tr('Vaccine Catalog')}</span>
                    <select className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition focus:border-slate-300 focus:outline-none" value={form.vaccineCode || ''} onChange={(event) => applyCatalogPick(event.target.value)}>
                      <option value="">{tr('Select from catalog (optional)...')}</option>
                      {catalog.map((item) => (
                        <option key={item.cvx} value={item.cvx}>{item.name} ({item.cvx})</option>
                      ))}
                    </select>
                  </label>
                  <div className="xl:col-span-4">
                    <Input label={tr('Vaccine Name')} value={form.vaccineName} onChange={(value) => setForm((prev) => ({ ...prev, vaccineName: value }))} />
                  </div>
                  <div className="xl:col-span-1">
                    <Input label={tr('CVX Code')} value={form.vaccineCode} onChange={(value) => setForm((prev) => ({ ...prev, vaccineCode: value }))} />
                  </div>
                  <div className="xl:col-span-2">
                    <Input label={tr('Disease')} value={form.disease} onChange={(value) => setForm((prev) => ({ ...prev, disease: value }))} />
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                title={tr('Administration')}
                description={tr('Capture when, how, and where the vaccine was given.')}
              >
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
                  <div className="space-y-3 xl:col-span-3">
                    <Select label={tr('Status')} value={form.status} options={statusOptions} onChange={(value) => setForm((prev) => ({ ...prev, status: value as VaccinationRecordStatus }))} />
                    <Input label={tr('Administration Date')} type="datetime-local" value={form.administrationDate} onChange={(value) => setForm((prev) => ({ ...prev, administrationDate: value }))} />
                    <Input label={tr('Recorded Date')} type="datetime-local" value={form.recordedDate} onChange={(value) => setForm((prev) => ({ ...prev, recordedDate: value }))} />
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:col-span-5">
                    <Input label={tr('Route')} value={form.route} onChange={(value) => setForm((prev) => ({ ...prev, route: value }))} />
                    <Input label={tr('Site')} value={form.site} onChange={(value) => setForm((prev) => ({ ...prev, site: value }))} />
                    <Select label={tr('Body Side')} value={form.bodySide} options={bodySideOptions} onChange={(value) => setForm((prev) => ({ ...prev, bodySide: value as VaccinationBodySide | '' }))} />
                    <Input label={tr('Dose Volume')} value={form.doseVolume} onChange={(value) => setForm((prev) => ({ ...prev, doseVolume: value }))} />
                    <div className="md:col-span-2">
                      <Input label={tr('Dose Unit')} value={form.doseUnit} onChange={(value) => setForm((prev) => ({ ...prev, doseUnit: value }))} />
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 shadow-sm xl:col-span-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Dose Tracking')}</p>
                    <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Input label={tr('Dose Number')} type="number" value={form.doseNumber} onChange={(value) => setForm((prev) => ({ ...prev, doseNumber: value }))} />
                      <Input label={tr('Series Doses')} type="number" value={form.seriesDoses} onChange={(value) => setForm((prev) => ({ ...prev, seriesDoses: value }))} />
                    </div>
                    <div className="mt-2">
                      <CompactToggle
                        label={tr('Booster')}
                        checked={form.booster}
                        text={tr('Booster dose')}
                        onChange={(checked) => setForm((prev) => ({ ...prev, booster: checked }))}
                      />
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                title={tr('Product And Source')}
                description={tr('Track the vial, manufacturer, clinic source, and inventory context.')}
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <Input label={tr('Lot Number')} value={form.lotNumber} onChange={(value) => setForm((prev) => ({ ...prev, lotNumber: value }))} />
                  <Input label={tr('Expiration')} type="datetime-local" value={form.expirationDate} onChange={(value) => setForm((prev) => ({ ...prev, expirationDate: value }))} />
                  <Input label={tr('Manufacturer')} value={form.manufacturer} onChange={(value) => setForm((prev) => ({ ...prev, manufacturer: value }))} />
                  <Input label={tr('Funding Source')} value={form.fundingSource} onChange={(value) => setForm((prev) => ({ ...prev, fundingSource: value }))} />
                  <Input label={tr('Administered By')} value={form.administeredBy} onChange={(value) => setForm((prev) => ({ ...prev, administeredBy: value }))} />
                  <Input label={tr('Location')} value={form.location} onChange={(value) => setForm((prev) => ({ ...prev, location: value }))} />
                  <Input label={tr('Source')} value={form.source} onChange={(value) => setForm((prev) => ({ ...prev, source: value }))} />
                  <Input label={tr('External ID')} value={form.externalId} onChange={(value) => setForm((prev) => ({ ...prev, externalId: value }))} />
                </div>
              </SectionCard>

              <SectionCard
                title={tr('Follow Up And Documentation')}
                description={tr('Keep due dates and VIS references in one place.')}
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <Input label={tr('VIS Date')} type="datetime-local" value={form.visDate} onChange={(value) => setForm((prev) => ({ ...prev, visDate: value }))} />
                  <Input label={tr('VIS Edition Date')} type="datetime-local" value={form.visEditionDate} onChange={(value) => setForm((prev) => ({ ...prev, visEditionDate: value }))} />
                  <Input label={tr('Next Due Date')} type="datetime-local" value={form.nextDueDate} onChange={(value) => setForm((prev) => ({ ...prev, nextDueDate: value }))} />
                </div>
              </SectionCard>

              <SectionCard
                title={tr('Notes')}
                description={tr('Document reactions and any clinical context for future review.')}
              >
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                  <TextArea label={tr('Reaction')} value={form.reaction} onChange={(value) => setForm((prev) => ({ ...prev, reaction: value }))} />
                  <TextArea label={tr('Clinical Notes')} value={form.note} onChange={(value) => setForm((prev) => ({ ...prev, note: value }))} />
                </div>
              </SectionCard>
            </div>

            <div className="sticky bottom-0 border-t border-slate-200 bg-white/95 px-5 py-4 backdrop-blur">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-slate-500">{tr('Review the grouped details, then save the vaccination record.')}</p>
                <div className="flex items-center gap-2">
                  <button className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-100" onClick={() => setDialogOpen(false)}>
                    {tr('Cancel')}
                  </button>
                  <button className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2 text-sm font-medium text-white shadow-sm disabled:opacity-60" onClick={saveRecord} disabled={saving}>
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {form.recordId ? tr('Update Record') : tr('Add Vaccine')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <input className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition focus:border-slate-300 focus:outline-none" value={value} onChange={(event) => onChange(event.target.value)} type={type} />
    </label>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const { tr } = useI18n();
  return (
    <label className="space-y-1 block">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <SelectMenu
        value={value}
        onChange={onChange}
        buttonAriaLabel={label}
        triggerClassName="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition"
        options={options.map((option) => ({ value: option, label: option ? tr(option) : '-' }))}
      />
    </label>
  );
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <textarea className="min-h-[132px] w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm shadow-sm transition focus:border-slate-300 focus:outline-none" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4">
        <h5 className="text-sm font-semibold text-slate-900">{title}</h5>
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      </div>
      {children}
    </section>
  );
}

function CompactToggle({
  label,
  text,
  checked,
  onChange,
}: {
  label: string;
  text: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="space-y-1">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm">
        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
        <span className="font-medium text-slate-800">{text}</span>
      </label>
    </div>
  );
}

function StatChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'blue' | 'emerald' | 'amber';
}) {
  const toneClass = {
    blue: 'border-blue-200 bg-blue-50 text-blue-800',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
  }[tone];

  return (
    <div className={`rounded-full border px-2.5 py-1.5 ${toneClass}`}>
      <span className="mr-1.5 text-[9px] uppercase tracking-wide opacity-75">{label}</span>
      <span className="text-xs font-medium">{value}</span>
    </div>
  );
}
